import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';

import Button from '@ui-elements/Button';

import ProgressTracker from '@ui-parts/ProgressTracker';
import TermsCheckBox from '@ui-parts/TermsCheckBox';
import InputWithLabel from '@ui-parts/InputWithLabel';
import SelectBoxWithLabel from '@ui-parts/SelectBoxWithLabel';

import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import { PALETTE, FONT_SIZES, TYPOGRAPHY, BASE_FONT_COLOR } from '~/assets/styles/base';
import { ROUTES } from '~/constants/routes';
import { USER_CATEGORY, INDIVIDUAL_CATEGORY, INDIVIDUAL_CATEGORY_NAME } from '~/common/constants';
import { PROGRESS_TRACKER_LABELS, PROGRESS_TYPE, USER_REGISTRATION_PROGRESSES } from '~/constants';
import { callThunk, getAddressesByZipCodeSignup } from '~/api';
import {
  emailValidationRule,
  passwordValidationRule,
  userNameValidationRule,
  userKanaNameValidationRule,
  companyNameValidationRule,
  departmentValidationRule,
  phoneValidationRule,
  zipCodeValidationRule,
  prefValidationRule,
  requiredAddressValidationRule,
} from '~/constants/validationRules';

const StyledForm = styled.form`
  background: ${PALETTE.white};
  width: 720px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 20px auto 55px;
  padding: 55px 90px;
  // バリデーションメッセージの改行制御
  .MuiFormControl-root div[color='error'] span {
    white-space: nowrap;
  }
`;

const FlexWrap = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const NameWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  // 横並びinput同時にバリデーションメッセージが出た場合、表示が重なるので改行を許可
  .MuiFormControl-root div[color='error'] span {
    white-space: normal;
  }
`;

const CompanyWrapper = styled.div`
  background: #f4f8fc;
  padding: 5px 10px;
  margin-top: 10px;
  margin-bottom: 20px;
`;

const StyledPolicyNote = styled.div`
  text-align: left;
  color: ${PALETTE.text.sub};
  font-size: ${FONT_SIZES.note1};
  white-space: pre-line;
  line-height: 1.8;
  background: ${PALETTE.blue.light};
  padding: 12px 9px 18px;
  margin-top: 10px;
`;

const StyledBasetext = styled.span`
  color: ${PALETTE.text.main};
`;

const TermsCheckBoxWrapper = styled.div`
  label {
    margin-right: 0;
  }
`;

const StyledLoginLink = styled(Link)`
  color: ${PALETTE.text.primary};
  font-size: ${FONT_SIZES.bodyDefault};
  text-decoration: none;
  padding-top: 5px;
`;
const StyledPolicyNoteContent = styled.div`
  a {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
  div {
    margin-top: 12px;
  }
`;

const StyledPolicyNoteContentFlex = styled.span`
  a {
    color: ${PALETTE.text.primary};
    text-decoration: none;
  }
`;

const CheckBoxWrapper = styled.div`
  width: 455px;
  margin-top: 30px;
  // ここのCheckBoxエラーtooltipが離れすぎているので調整
  div[color='error'] {
    margin-top: 6px;
    margin-bottom: 16px;
  }
`;

const ControlWrapper = styled.div`
  margin-bottom: 20px;
`;

const CenterBox = styled.div`
  width: 455px;
  margin: 0 auto;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: ${FONT_SIZES.body3};
  color: ${BASE_FONT_COLOR};
`;

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, appCommon } = useSelector((state) => state);
  const [individualCategory, setIndividualCategory] = useState(user.individualCategory);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [gmoSignChecked, setGmoSignChecked] = useState(false);

  const { control, handleSubmit, setValue, getValues } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      individualCategory: '',
      familyName: '',
      firstName: '',
      kanaFamilyName: '', // フリガナはAPI送信しないが、名前の漢字入力促進の施策として設置 バリデーションもする
      kanaFirstName: '',
      email: '',
      password: '',
      confirmPassword: '',
      company: '',
      department: '',
      phone: '',
      zip: '',
      prefecture: '',
      address: '',
    },
  });

  const getAddress = () => {
    if (getValues('zip').match(zipCodeValidationRule.pattern.value) != null) {
      dispatch(
        callThunk({
          action: getAddressesByZipCodeSignup,
          payload: getValues('zip'),
          skipLoading: true,
        })
      );
    }
  };

  const onSubmit = (data) => {
    dispatch({
      type: 'user/change',
      payload: {
        user: {
          name: data.familyName + data.firstName,
          email: data.email,
          password: data.password,
          confirmPassword: data.confirmPassword,
          // individualCategoryの値によって、categoryの値を選定
          category:
            Number(data.individualCategory) === INDIVIDUAL_CATEGORY.CORPORATE
              ? USER_CATEGORY.CORPORATE
              : USER_CATEGORY.INDIVIDUAL,
          individualCategory: Number(data.individualCategory),
          companyName: data.company,
          departmentName: data.department,
          phone: data.phone,
          zip: data.zip,
          pref: data.prefecture,
          address: data.address,
        },
      },
    });
    dispatch({
      type: 'appCommon/changeTerms',
      payload: {
        userRegistration: {
          terms: true,
          sign: true,
        },
      },
    });
    // navigate(ROUTES.USER.SIGNUP_CONFIRM);
    // 確認画面の戻るボタンから戻って来る際の値復元の為に確認画面に名前に関するstateを渡す
    navigate(ROUTES.USER.SIGNUP_CONFIRM, {
      state: {
        familyName: data.familyName,
        firstName: data.firstName,
        kanaFamilyName: data.kanaFamilyName,
        kanaFirstName: data.kanaFirstName,
      },
    });
  };

  useEffect(() => {
    // 名前系のinputはstoreのuserに無いのでlocationより復元
    setValue('familyName', location?.state?.familyName);
    setValue('firstName', location?.state?.firstName);
    setValue('kanaFamilyName', location?.state?.kanaFamilyName);
    setValue('kanaFirstName', location?.state?.kanaFirstName);
    // categoryはデフォルト値があり、必須バリデーションが存在しないのでsetValueしない
    setValue('individualCategory', user.individualCategory.toString());
    setValue('password', user.password);
    setValue('confirmPassword', user.confirmPassword);
    setValue('email', user.email);
    setValue('company', user.companyName);
    setValue('department', user.departmentName);
    setValue('phone', user.phone);
    setValue('zip', user.zip);
    setValue('prefecture', user.pref);
    setValue('address', user.address);

    if (appCommon.isChecked) {
      setPrivacyChecked(appCommon.isChecked.userRegistration.terms);
      setGmoSignChecked(appCommon.isChecked.userRegistration.sign);
    }
  }, []);

  useEffect(() => {
    if (user.pref) {
      setValue('prefecture', user.pref);
      setValue('address', user.address);
    }
  }, [user.pref, user.address]);

  return (
    <>
      <ProgressTracker
        width="618px"
        labels={PROGRESS_TRACKER_LABELS[PROGRESS_TYPE.USER_REGISTRATION].map((label) => t(label))}
        currentNum={USER_REGISTRATION_PROGRESSES[ROUTES.USER.SIGNUP]}
      />

      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Typography
          sx={{
            color: PALETTE.text.primary,
            fontSize: TYPOGRAPHY.title1,
            textAlign: 'center',
            marginBottom: '10px',
          }}
          data-testid="pages-signup-title"
        >
          {t('registration.title')}
        </Typography>
        <FlexWrap>
          <Typography
            sx={{
              color: PALETTE.text.main,
              fontSize: TYPOGRAPHY.bodyDefault,
              textAlign: 'center',
              paddingLeft: '100px',
            }}
            data-testid="pages-signup-alreadyhaveaccount"
          >
            {t('registration.alreadyHaveAccount')}
          </Typography>
          <StyledLoginLink
            component={RouterLink}
            to={ROUTES.USER.SIGNIN}
            data-testid="pages-signup-loginbtn"
          >
            {t('registration.login')}
          </StyledLoginLink>
        </FlexWrap>
        <CenterBox>
          <ControlWrapper>
            <Controller
              name="email"
              control={control}
              rules={emailValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  label={t('registration.email')}
                  placeholder=""
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-email"
                  required
                  width="455px"
                  inputProps={{ maxLength: 100 }}
                  ref={ref}
                />
              )}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Controller
              name="password"
              control={control}
              rules={passwordValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  type="password"
                  label={t('registration.password')}
                  placeholder=""
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-password"
                  required
                  ref={ref}
                  width="455px"
                  inputProps={{ maxLength: 24 }}
                  description={t('registration.passwordNote')}
                  background={PALETTE.bg.main}
                  fontSize="note2"
                  padding="15px 10px"
                />
              )}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                ...passwordValidationRule,
                validate: {
                  message: (v) => (v === getValues('password') ? null : t('validation.pswNotSame')),
                },
              }}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  type="password"
                  label={t('registration.confirmPassword')}
                  placeholder=""
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-confirmpassword"
                  required
                  ref={ref}
                  width="455px"
                  inputProps={{ maxLength: 24 }}
                />
              )}
            />
          </ControlWrapper>
          <ControlWrapper>
            <NameWrapper>
              <Controller
                name="familyName"
                control={control}
                rules={userNameValidationRule}
                render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                  <InputWithLabel
                    label="姓：漢字"
                    placeholder=""
                    name={name}
                    value={value}
                    handleChange={(e) => {
                      onChange(e.target.value);
                    }}
                    handleBlur={(e) => {
                      setValue(name, e.target.value.trim());
                    }}
                    handleOnKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setValue(name, e.target.value.trim());
                      }
                    }}
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    dataTestId="pages-signup-familyName"
                    required
                    ref={ref}
                    width="210px"
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              />
              <Controller
                name="firstName"
                control={control}
                rules={userNameValidationRule}
                render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                  <InputWithLabel
                    label="名：漢字"
                    placeholder=""
                    name={name}
                    value={value}
                    handleChange={(e) => {
                      onChange(e.target.value);
                    }}
                    handleBlur={(e) => {
                      setValue(name, e.target.value.trim());
                    }}
                    handleOnKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setValue(name, e.target.value.trim());
                      }
                    }}
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    dataTestId="pages-signup-firstName"
                    required
                    ref={ref}
                    width="210px"
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              />
            </NameWrapper>
            <NameWrapper>
              <Controller
                name="kanaFamilyName"
                control={control}
                rules={userKanaNameValidationRule}
                render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                  <InputWithLabel
                    label="セイ：カタカナ"
                    placeholder=""
                    name={name}
                    value={value}
                    handleChange={(e) => {
                      onChange(e.target.value);
                    }}
                    handleBlur={(e) => {
                      setValue(name, e.target.value.trim());
                    }}
                    handleOnKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setValue(name, e.target.value.trim());
                      }
                    }}
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    dataTestId="pages-signup-kanaFamilyName"
                    required
                    ref={ref}
                    width="210px"
                  />
                )}
              />
              <Controller
                name="kanaFirstName"
                control={control}
                rules={userKanaNameValidationRule}
                render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                  <InputWithLabel
                    label="メイ：カタカナ"
                    placeholder=""
                    name={name}
                    value={value}
                    handleChange={(e) => {
                      onChange(e.target.value);
                    }}
                    handleBlur={(e) => {
                      setValue(name, e.target.value.trim());
                    }}
                    handleOnKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        setValue(name, e.target.value.trim());
                      }
                    }}
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    dataTestId="pages-signup-kanaFirstName"
                    required
                    ref={ref}
                    width="210px"
                  />
                )}
              />
            </NameWrapper>
          </ControlWrapper>
          <ControlWrapper>
            <Controller
              name="individualCategory"
              control={control}
              rules={{ required: t('validation.requiredChoice') }}
              render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <SelectBoxWithLabel
                    handleChange={(e) => {
                      onChange(e.target.value);
                      setIndividualCategory(e.target.value);
                    }}
                    label={t('registration.category')}
                    value={value}
                    name={name}
                    error={error}
                    required
                  >
                    <StyledMenuItem value="">
                      {t('registration.user.individualCategory.unselected')}
                    </StyledMenuItem>
                    {Object.values(INDIVIDUAL_CATEGORY).map((categoryNum) => {
                      return (
                        <StyledMenuItem key={categoryNum} value={categoryNum.toString()}>
                          {INDIVIDUAL_CATEGORY_NAME[categoryNum]}
                        </StyledMenuItem>
                      );
                    })}
                  </SelectBoxWithLabel>
                </FormControl>
              )}
            />
          </ControlWrapper>
          {individualCategory !== '' &&
          Number(individualCategory) === INDIVIDUAL_CATEGORY.CORPORATE ? (
            <CompanyWrapper>
              <ControlWrapper>
                <Controller
                  name="company"
                  control={control}
                  rules={companyNameValidationRule}
                  render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                    <InputWithLabel
                      label={t('registration.company')}
                      placeholder="GMOリサーチ&AI株式会社"
                      name={name}
                      value={value}
                      handleChange={(e) => {
                        onChange(e.target.value);
                      }}
                      handleBlur={(e) => {
                        setValue(name, e.target.value.trim());
                      }}
                      handleOnKeyDown={(e) => {
                        if (e.keyCode === 13) {
                          setValue(name, e.target.value.trim());
                        }
                      }}
                      error={Boolean(error)}
                      errorMessage={error?.message}
                      dataTestId="pages-signup-company"
                      required
                      ref={ref}
                      width="430px"
                      inputProps={{ maxLength: 80 }}
                    />
                  )}
                />
              </ControlWrapper>
              <ControlWrapper>
                <Controller
                  name="department"
                  control={control}
                  rules={departmentValidationRule}
                  render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                    <InputWithLabel
                      label={t('registration.department')}
                      placeholder="営業部"
                      name={name}
                      value={value}
                      handleChange={(e) => {
                        onChange(e.target.value);
                      }}
                      handleBlur={(e) => {
                        setValue(name, e.target.value.trim());
                      }}
                      handleOnKeyDown={(e) => {
                        if (e.keyCode === 13) {
                          setValue(name, e.target.value.trim());
                        }
                      }}
                      error={Boolean(error)}
                      errorMessage={error?.message}
                      dataTestId="pages-signup-department"
                      required
                      ref={ref}
                      width="430px"
                      inputProps={{ maxLength: 100 }}
                    />
                  )}
                />
              </ControlWrapper>
            </CompanyWrapper>
          ) : null}
          <FlexWrap>
            <Controller
              name="zip"
              control={control}
              rules={zipCodeValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  label={t('registration.zip')}
                  placeholder="1500031"
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                    getAddress();
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-zip"
                  required
                  ref={ref}
                  width="130px"
                  inputProps={{ maxLength: 7 }}
                />
              )}
            />
          </FlexWrap>
          <ControlWrapper>
            <Controller
              name="prefecture"
              control={control}
              rules={prefValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  label={t('registration.prefecture')}
                  placeholder=""
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-pref"
                  required
                  width="130px"
                  ref={ref}
                  inputProps={{ maxLength: 32 }}
                />
              )}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Controller
              name="address"
              control={control}
              rules={requiredAddressValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  label={t('registration.address')}
                  placeholder=""
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-address"
                  required
                  width="455px"
                  inputProps={{ maxLength: 128 }}
                  ref={ref}
                />
              )}
            />
          </ControlWrapper>
          <ControlWrapper>
            <Controller
              name="phone"
              control={control}
              rules={phoneValidationRule}
              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                <InputWithLabel
                  label={t('registration.phone')}
                  placeholder="0359620037"
                  name={name}
                  value={value}
                  handleChange={(e) => {
                    onChange(e.target.value);
                  }}
                  handleBlur={(e) => {
                    setValue(name, e.target.value.trim());
                  }}
                  handleOnKeyDown={(e) => {
                    if (e.keyCode === 13) {
                      setValue(name, e.target.value.trim());
                    }
                  }}
                  error={Boolean(error)}
                  errorMessage={error?.message}
                  dataTestId="pages-signup-phone"
                  required
                  ref={ref}
                  width="430px"
                  inputProps={{ maxLength: 11 }}
                />
              )}
            />
          </ControlWrapper>
          <CheckBoxWrapper>
            <Controller
              name="privacyChecked"
              control={control}
              rules={{
                validate: {
                  message: () => (privacyChecked ? null : t('validation.policyAgree')),
                },
              }}
              render={({ fieldState: { error } }) => (
                <TermsCheckBoxWrapper>
                  <TermsCheckBox
                    label={
                      <StyledPolicyNoteContentFlex>
                        <a
                          href={ROUTES.COMMON.ASK_POLICY}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setPrivacyChecked(true)}
                          data-testid="pages-signup-privacy-labellink"
                        >
                          {t('registration.privacyPolicy')}
                        </a>
                        <StyledBasetext>{t('registration.policyAgree')}</StyledBasetext>
                      </StyledPolicyNoteContentFlex>
                    }
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    checked={privacyChecked}
                    disabled={!privacyChecked}
                    handleChange={(e) => setPrivacyChecked(e.target.checked)}
                    dataTestId="pages-signup-privacy"
                  />
                </TermsCheckBoxWrapper>
              )}
            />
            <Controller
              name="gmoSignChecked"
              control={control}
              rules={{
                validate: {
                  message: () => (gmoSignChecked ? null : t('validation.policyAgree')),
                },
              }}
              render={({ fieldState: { error } }) => (
                <TermsCheckBoxWrapper>
                  <TermsCheckBox
                    label={t('registration.gmoSignAgree')}
                    error={Boolean(error)}
                    errorMessage={error?.message}
                    checked={gmoSignChecked}
                    handleChange={(e) => setGmoSignChecked(e.target.checked)}
                    dataTestId="pages-signup-gmoSignAgree"
                  />
                </TermsCheckBoxWrapper>
              )}
            />
          </CheckBoxWrapper>
          <StyledPolicyNote>
            <StyledPolicyNoteContent
              dangerouslySetInnerHTML={{
                __html: t('registration.policyMessage'),
              }}
              data-testid="pages-signup-policynote"
            />
          </StyledPolicyNote>
        </CenterBox>
        <Box sx={{ textAlign: 'center', marginTop: '55px' }}>
          <Button
            type="submit"
            variant="contained"
            data-testid="pages-signup-confirmButton"
            sx={{ width: 148 }}
          >
            {t('appCommon.confirm')}
          </Button>
        </Box>
      </StyledForm>
    </>
  );
}
export default Signup;
